import { create } from 'zustand';
import type { User } from 'firebase/auth';
import type { UserProfile, CreateUserInput, PlayerRace } from '@kaeliux/shared';
import * as authService from '../services/auth.service';
import type { AuthResult } from '../services/authSession.types';
import { describeSession, type AuthSessionInfo } from '../services/authSession';
import { ACCOUNT_EXISTS_CODE, authErrorCode, authErrorEmail, authErrorMessage } from '../services/authErrors';
import { SOCIAL_PROVIDER_NAMES, type SocialProvider } from '../services/authProviders';
import { getUserProfile } from '../services/profile.service';
import { clearDismissedAccountNotice } from '../utils/accountNoticeDismissal';
import { usePlanetStore } from './planetStore';
import { useUniverseStore } from './universeStore';

/** A provider sign-in refused because its email already has an account; linked on the next matching sign-in. */
export interface PendingLink {
    email: string;
    provider: SocialProvider;
}

interface AuthState {
    user: User | null;
    profile: UserProfile | null;
    session: AuthSessionInfo | null;
    /** Only true during the initial session check — sign-in actions track their own pending state. */
    isLoading: boolean;
    error: string | null;
    pendingLink: PendingLink | null;
    login: () => Promise<void>;
    register: (data: CreateUserInput) => Promise<void>;
    socialLogin: (provider: SocialProvider) => Promise<void>;
    passwordSignIn: (email: string, password: string) => Promise<void>;
    passwordSignUp: (email: string, password: string) => Promise<void>;
    /** `getToken` supplies a Turnstile token for guest sign-up; its failures surface as the auth error. */
    guestSignIn: (getToken: () => Promise<string>) => Promise<void>;
    signInWithGuestId: (guestId: string, getToken: () => Promise<string>) => Promise<void>;
    completeEmailLinkSignIn: (email: string, href: string) => Promise<void>;
    sendPasswordReset: (email: string) => Promise<void>;
    sendEmailSignInLink: (email: string) => Promise<void>;
    upgradeGuestWithPassword: (email: string, password: string) => Promise<void>;
    upgradeGuestWithProvider: (provider: SocialProvider) => Promise<void>;
    resendVerificationEmail: () => Promise<void>;
    /** Re-reads the account (e.g. after email verification) and returns the fresh session flags. */
    refreshSession: () => Promise<AuthSessionInfo | null>;
    cancelPendingLink: () => void;
    logout: () => Promise<void>;
    refreshProfile: () => Promise<void>;
    clearError: () => void;
    setRace: (race: PlayerRace) => Promise<void>;
}

const sessionState = ({ user, profile }: AuthResult) => ({ user, profile, session: describeSession(user) });

export const useAuthStore = create<AuthState>((set, get) => {
    /**
     * Shared by every action that starts a new session. Deliberately leaves
     * `isLoading` alone: App.tsx swaps the whole tree for a spinner while it
     * is true, which would unmount the form (and its typed email) mid-submit.
     *
     * The universe pick is cleared *before* signing in: real Firebase fires
     * the auth listener before the sign-in promise resolves, so the picker can
     * already be on screen — clearing afterwards would wipe a fresh pick.
     */
    const startSession = async (signIn: () => Promise<AuthResult>, provider?: SocialProvider) => {
        set({ error: null });
        const universes = useUniverseStore.getState();
        const previousUniverseId = universes.selectedUniverseId;
        universes.clearSelectedUniverse();
        try {
            const result = await signIn();
            set({ ...sessionState(result), pendingLink: null });
        } catch (error: unknown) {
            if (previousUniverseId && !get().user) {
                useUniverseStore.getState().commitSelectedUniverse(previousUniverseId);
            }
            const conflictEmail = authErrorCode(error) === ACCOUNT_EXISTS_CODE ? authErrorEmail(error) : undefined;
            if (provider && conflictEmail && authService.hasPendingLink()) {
                set({ pendingLink: { email: conflictEmail, provider } });
            } else {
                set({ error: authErrorMessage(error, provider && SOCIAL_PROVIDER_NAMES[provider]) });
            }
            throw error;
        }
    };

    /** Guest upgrades keep the same uid and universe, so the player stays exactly where they are. */
    const replaceAccount = async (upgrade: () => Promise<AuthResult>) => {
        set(sessionState(await upgrade()));
    };

    return {
        user: null,
        profile: null,
        session: null,
        isLoading: authService.initialLoadingState(),
        error: null,
        pendingLink: null,

        login: () => startSession(authService.login),
        register: (data) => startSession(() => authService.register(data)),
        socialLogin: (provider) => startSession(() => authService.socialLogin(provider), provider),
        passwordSignIn: (email, password) => startSession(() => authService.passwordSignIn(email, password)),
        passwordSignUp: (email, password) => startSession(() => authService.passwordSignUp(email, password)),
        guestSignIn: (getToken) => startSession(async () => authService.guestSignIn(await getToken())),
        signInWithGuestId: (guestId, getToken) =>
            startSession(async () => authService.signInWithGuestId(guestId, await getToken())),
        completeEmailLinkSignIn: (email, href) => startSession(() => authService.completeEmailLinkSignIn(email, href)),

        sendPasswordReset: (email) => authService.sendPasswordReset(email),
        sendEmailSignInLink: (email) => authService.sendEmailSignInLink(email),

        upgradeGuestWithPassword: (email, password) =>
            replaceAccount(() => authService.upgradeGuestWithPassword(email, password)),
        upgradeGuestWithProvider: (provider) => replaceAccount(() => authService.upgradeGuestWithProvider(provider)),

        resendVerificationEmail: () => authService.resendVerificationEmail(),

        refreshSession: async () => {
            const user = await authService.refreshUser();
            if (!user) return null;
            const session = describeSession(user);
            set({ user, session });
            return session;
        },

        cancelPendingLink: () => {
            authService.cancelPendingLink();
            set({ pendingLink: null });
        },

        logout: async () => {
            set({ isLoading: true });
            try {
                await authService.logoutSession();
                // Drop the session's universe pick too — issue #53 wants the
                // picker shown "on every login", not remembered indefinitely.
                useUniverseStore.getState().clearSelectedUniverse();
                clearDismissedAccountNotice();
                set({ user: null, profile: null, session: null, pendingLink: null, isLoading: false });
            } catch (error: unknown) {
                set({ error: authErrorMessage(error), isLoading: false });
                throw error;
            }
        },

        refreshProfile: async () => {
            const { user } = get();
            if (!user) {
                set({ profile: null });
                return;
            }

            try {
                const profile = await getUserProfile(user.uid);
                set({ profile });
            } catch (error: unknown) {
                console.error('Failed to refresh profile:', error);
            }
        },

        clearError: () => set({ error: null }),

        /**
         * Sets the race chosen by the player upon first planet creation.
         * Race is only ever picked together with the first planet —
         * `createPlanet` (Cloud Function) persists both onto the
         * server-owned `universes/{u}/players/{uid}` doc in one call, so
         * there's no separate client write for race anymore.
         *
         * @param race Player's chosen race, "humans" | "robots" | "axons"
         */
        setRace: async (race: PlayerRace) => {
            const { user, profile } = get();
            if (!user) throw new Error('No user logged in');

            set({ error: null });
            try {
                if (!profile?.mainPlanetId) {
                    await usePlanetStore.getState().createInitialPlanet(user.uid, race);
                }

                await get().refreshProfile();
            } catch (error: unknown) {
                set({ error: error instanceof Error ? error.message : String(error) });
                throw error;
            }
        },
    };
});

authService.subscribeToAuthChanges((result) => {
    if (result) {
        const current = useAuthStore.getState();
        const sameAccount = current.user?.uid === result.user.uid;
        // A different account signed in (e.g. from another tab): its universe
        // isn't known yet, and the previous account's pick must not carry over.
        if (current.user && !sameAccount) useUniverseStore.getState().clearSelectedUniverse();
        useAuthStore.setState({
            ...sessionState(result),
            // This read can land after the sign-in action already fetched (or
            // created) the profile; don't let a stale empty read erase it.
            profile: result.profile ?? (sameAccount ? current.profile : null),
            isLoading: false,
        });
    } else {
        useUniverseStore.getState().clearSelectedUniverse();
        useAuthStore.setState({ user: null, profile: null, session: null, isLoading: false });
    }
});
